// EP-111 T111-8 — the CAF sidebar page (docs/ep-111-condominium-property-type.md
// D5/D7/D8, sidebar item directly below Meters). "Meter-reading style": pick a
// condominium property, see the currently active rate + read-only method
// context (the STRUCTURE — fee_type/calculation_method — stays on the
// property's CAF tab), supersede it via "Set new rate" (T111-4),
// and review the per-unit breakdown + the effective-dated rate history.
//
// D10 (CRITICAL) — the per-unit preview must match the invoice-emission skip:
// a per_sqm/per_share_ratio unit with area/ratio 0 (or unset) gets NO CAF line
// on the real invoice (PricingCalculator#calculate_common_area_fee_amount
// returns nil and the whole line is dropped — api/app/services/pricing_calculator.rb).
// The CommonAreaFeesController#preview endpoint does NOT apply that skip (it
// always renders `rate * unit.area_sqm.to_f`, i.e. ฿0.00 for a zero/nil area),
// so this page overrides the display for exactly those units with a warning
// row instead of a misleading ฿0.00 — see isZeroBasisUnit below.
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Coins, History, Plus } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Alert, Badge, Button, Card, EmptyState, Select, Skeleton } from '@/components/ui'
import { SetCafRateModal } from '@/components/common-area-fees/SetCafRateModal'
import { useAllProperties } from '@/hooks/useProperties'
import { useCommonAreaFees, useCommonAreaFeePreview } from '@/hooks/useCommonAreaFees'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { formatBaht } from '@/lib/money'
import type { CommonAreaFee, CommonAreaFeePreviewUnit } from '@/types'

interface FeeSeries {
  seriesId: number
  /** All rows of the series, newest effective_from first. */
  rows: CommonAreaFee[]
  /** The open row (effective_until === null), if any. */
  active: CommonAreaFee | undefined
}

// Group a property's fee rows (both active-only and full-history responses
// funnel through here) by series_id — a property may run several fee series
// at once (maintenance_fund + sinking_fund + insurance), each superseded
// independently (D8/F-B).
function groupBySeries(fees: CommonAreaFee[]): FeeSeries[] {
  const map = new Map<number, CommonAreaFee[]>()
  fees.forEach((fee) => {
    const key = fee.series_id ?? fee.id
    const bucket = map.get(key)
    if (bucket) bucket.push(fee)
    else map.set(key, [fee])
  })
  return Array.from(map.entries()).map(([seriesId, rows]) => {
    const sorted = [...rows].sort((a, b) => (a.effective_from < b.effective_from ? 1 : -1))
    return { seriesId, rows: sorted, active: sorted.find((r) => r.effective_until === null) }
  })
}

// D10 — a per_sqm/per_share_ratio unit with no area/ratio (0 or unset) is
// skipped entirely at invoice time (see file header). Mirrors
// PricingCalculator#calculate_common_area_fee_amount's `return nil if
// sqm.zero?` / `return nil if ratio.zero?` guard so the preview never shows
// a misleading ฿0.00 for a unit that will actually get no CAF line.
function isZeroBasisUnit(unit: CommonAreaFeePreviewUnit, calculationMethod: string): boolean {
  if (calculationMethod === 'per_sqm') return !unit.area_sqm || unit.area_sqm === 0
  if (calculationMethod === 'per_share_ratio') return !unit.share_ratio || unit.share_ratio === 0
  return false
}

function FeeSeriesCard({
  propertyId,
  series,
  onSetRate,
}: {
  propertyId: number
  series: FeeSeries
  onSetRate: (fee: CommonAreaFee) => void
}) {
  const { t } = useTranslation('common_area_fees')
  const formatDate = useDateFormatter()

  const contextFee = series.active ?? series.rows[0]
  const previewEnabled = !!series.active
  const { data: preview, isLoading: isLoadingPreview } = useCommonAreaFeePreview(
    propertyId,
    {
      calculation_method: contextFee.calculation_method,
      rate: Number(contextFee.rate),
    },
    previewEnabled,
  )

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Coins className="w-5 h-5 text-base-content/60" />
            <h3 className="text-lg font-semibold">{contextFee.name}</h3>
          </div>
          <p className="text-sm text-base-content/60 mt-1">
            {t('rates_page.method_context', {
              fee_type: t(`fee_types.${contextFee.fee_type}`),
              method: t(`calculation_methods.${contextFee.calculation_method}`),
              vat: Number(contextFee.vat_rate),
            })}
          </p>
        </div>
        <Button
          size="sm"
          leftIcon={<Plus className="w-4 h-4" />}
          data-testid={`set-new-rate-${series.seriesId}`}
          onClick={() => onSetRate(contextFee)}
        >
          {t('rates_page.set_new_rate')}
        </Button>
      </div>

      {/* Active rate */}
      {series.active ? (
        <div className="flex items-center gap-3 mb-6" data-testid={`active-rate-${series.seriesId}`}>
          <span className="text-2xl font-bold text-primary">{formatBaht(Number(series.active.rate))}</span>
          <span className="text-sm text-base-content/60">{t(`calculation_methods.${series.active.calculation_method}`)}</span>
          <Badge variant="success">{t('rates_page.open')}</Badge>
          <span className="text-sm text-base-content/60">
            {t('rates_page.effective_since', { date: formatDate(series.active.effective_from) })}
          </span>
        </div>
      ) : (
        <Alert variant="warning" className="mb-6">
          {t('rates_page.no_active_rate')}
        </Alert>
      )}

      {/* Per-unit breakdown */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold mb-2">{t('rates_page.per_unit_breakdown')}</h4>
        {!previewEnabled ? (
          <p className="text-sm text-base-content/60">{t('rates_page.no_active_rate')}</p>
        ) : isLoadingPreview ? (
          <div className="space-y-2">
            <Skeleton className="h-8" />
            <Skeleton className="h-8" />
          </div>
        ) : !preview || preview.units.length === 0 ? (
          <p className="text-sm text-base-content/60">{t('preview.empty')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="table table-sm w-full">
              <thead>
                <tr>
                  <th>{t('labels.unit_number')}</th>
                  <th className="text-right">{t('labels.calculated_amount')}</th>
                </tr>
              </thead>
              <tbody>
                {preview.units.map((unit) => {
                  const zeroBasis = isZeroBasisUnit(unit, contextFee.calculation_method)
                  return (
                    <tr key={unit.unit_id} data-testid={`preview-row-${unit.unit_id}`}>
                      <td className="font-medium">{unit.unit_name || unit.unit_number}</td>
                      <td className="text-right">
                        {zeroBasis ? (
                          <span className="text-warning text-sm" data-testid={`zero-basis-warning-${unit.unit_id}`}>
                            {t(
                              contextFee.calculation_method === 'per_share_ratio'
                                ? 'rates_page.zero_ratio_warning'
                                : 'rates_page.zero_area_warning',
                            )}
                          </span>
                        ) : (
                          formatBaht(unit.amount, { decimals: 2 })
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Rate history */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <History className="w-4 h-4 text-base-content/60" />
          <h4 className="text-sm font-semibold">{t('rates_page.rate_history')}</h4>
        </div>
        <ul className="space-y-1" data-testid={`rate-history-${series.seriesId}`}>
          {series.rows.map((row) => {
            const isOpen = row.effective_until === null
            return (
              <li key={row.id} className="flex items-center gap-2 text-sm" data-testid={`history-row-${row.id}`}>
                <Badge variant={isOpen ? 'success' : 'ghost'}>
                  {isOpen ? t('rates_page.open') : t('rates_page.closed')}
                </Badge>
                <span className="font-medium">{formatBaht(Number(row.rate))}</span>
                <span className="text-base-content/60">
                  {formatDate(row.effective_from)} →{' '}
                  {row.effective_until ? formatDate(row.effective_until) : t('rates_page.open')}
                </span>
              </li>
            )
          })}
        </ul>
      </div>
    </Card>
  )
}

export function CommonAreaFeeRatesPage() {
  const { t } = useTranslation('common_area_fees')

  const { data: allProperties, isLoading: isLoadingProperties } = useAllProperties()
  const condoProperties = useMemo(
    () => (allProperties ?? []).filter((p) => p.property_type === 'condominium'),
    [allProperties],
  )

  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(null)

  // Default to the first condo property once the list loads (and re-pick if
  // the previously-selected property drops out of the condo list, e.g.
  // downgraded elsewhere while this page is open).
  useEffect(() => {
    if (condoProperties.length === 0) {
      setSelectedPropertyId(null)
      return
    }
    if (!condoProperties.some((p) => p.id === selectedPropertyId)) {
      setSelectedPropertyId(condoProperties[0].id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condoProperties])

  const propertyId = selectedPropertyId ?? 0
  const { data: fees, isLoading: isLoadingFees, isFetching: isFetchingFees } = useCommonAreaFees(
    propertyId,
    true,
  )

  const feeSeries = useMemo(() => groupBySeries(fees ?? []), [fees])

  const [rateModalFee, setRateModalFee] = useState<CommonAreaFee | null>(null)

  const isLoading = isLoadingProperties || (!!selectedPropertyId && isLoadingFees)

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: t('rates_page.title') }]} />

        <PageHeader
          title={t('rates_page.title')}
          description={t('rates_page.description')}
          isRefreshing={isFetchingFees && !isLoadingFees}
          primaryAction={
            feeSeries.length > 0
              ? {
                  key: 'set-new-rate',
                  label: t('rates_page.set_new_rate'),
                  icon: Plus,
                  onClick: () => setRateModalFee(feeSeries[0].active ?? feeSeries[0].rows[0]),
                }
              : undefined
          }
        />

        {isLoadingProperties ? (
          <Card>
            <Skeleton className="h-24 w-full" />
          </Card>
        ) : condoProperties.length === 0 ? (
          <Card>
            <EmptyState
              icon={<Coins className="w-12 h-12" />}
              title={t('rates_page.no_condo_properties_title')}
              description={t('rates_page.no_condo_properties_description')}
            />
          </Card>
        ) : (
          <>
            <Card>
              <Select
                label={t('rates_page.property_label')}
                data-testid="caf-property-picker"
                value={selectedPropertyId ? String(selectedPropertyId) : ''}
                onChange={(e) => setSelectedPropertyId(e.target.value ? Number(e.target.value) : null)}
                options={condoProperties.map((p) => ({ value: String(p.id), label: p.name }))}
              />
            </Card>

            {isLoading ? (
              <Card>
                <Skeleton className="h-64 w-full" />
              </Card>
            ) : feeSeries.length === 0 ? (
              <Card>
                <EmptyState
                  icon={<Coins className="w-12 h-12" />}
                  title={t('rates_page.no_fees_configured_title')}
                  description={t('rates_page.no_fees_configured_description')}
                  action={
                    selectedPropertyId ? (
                      <Button as={Link} size="sm" to={`/properties/${selectedPropertyId}?tab=caf`}>
                        {t('rates_page.configure_on_caf_tab')}
                      </Button>
                    ) : undefined
                  }
                />
              </Card>
            ) : (
              <div className="space-y-6">
                {feeSeries.map((series) => (
                  <FeeSeriesCard
                    key={series.seriesId}
                    propertyId={propertyId}
                    series={series}
                    onSetRate={setRateModalFee}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <SetCafRateModal
        isOpen={rateModalFee !== null}
        onClose={() => setRateModalFee(null)}
        propertyId={propertyId}
        fee={rateModalFee}
        onSuccess={() => setRateModalFee(null)}
      />
    </>
  )
}
